import { useMemo, useState } from "react";

import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { isEmpty } from "lodash";

import { AUDIT_DIFF_TYPE, auditDiffToText, auditFieldLabel, parseAuditDiff } from "@plannel/pages/utils/audit-diff-util";
import ColorUtil from "@plannel/pages/utils/color-util";
import { FS } from "@plannel/utils/font-util";

/*
  Audit Trail 목록의 "변경 내역" 셀.

  예전에는 oldValue / newValue 원본 JSON 을 컬럼 두 개에 통째로 뿌렸다. 한 줄에 담기지 않아
  실제로 읽히는 건 `{"id":123,"crea...` 뿐이었고, 무엇이 바뀌었는지는 팝업을 열어야만 알 수 있었다.
  여기서는 diff 를 목록 단계에서 `필드 이전값 → 이후값` 으로 요약해, 팝업을 열지 않고도 훑어볼 수 있게 한다.
  한 줄 안에 들어가야 하므로 앞 3건만 보여주고 나머지는 `+N`, 전체는 툴팁으로 넘긴다.
*/

const MAX_PREVIEW_ITEMS = 3;
const MAX_VALUE_LENGTH = 18;
const EMPTY_MARK = "—";

const { auditDiffColor } = ColorUtil;

const shorten = (value) => {
  if (isEmpty(value) || value === "null") {
    return EMPTY_MARK;
  }

  return value.length > MAX_VALUE_LENGTH ? `${value.slice(0, MAX_VALUE_LENGTH)}…` : value;
};

const valueSx = (color) => ({ color, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" });

const ChangeItem = ({ row }) => (
  <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, minWidth: 0 }}>
    <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>{auditFieldLabel(row.field)}</Box>
    {row.type === AUDIT_DIFF_TYPE.REMOVED && (
      <Box component="span" sx={valueSx(auditDiffColor.removed)}>{`(-) ${shorten(row.oldValue)}`}</Box>
    )}
    {row.type === AUDIT_DIFF_TYPE.ADDED && (
      <Box component="span" sx={valueSx(auditDiffColor.added)}>{`(+) ${shorten(row.newValue)}`}</Box>
    )}
    {row.type === AUDIT_DIFF_TYPE.CHANGED && (
      <>
        <Box component="span" sx={valueSx(auditDiffColor.removed)}>{shorten(row.oldValue)}</Box>
        <Box component="span" sx={{ color: "text.disabled" }}>→</Box>
        <Box component="span" sx={valueSx(auditDiffColor.added)}>{shorten(row.newValue)}</Box>
      </>
    )}
  </Box>
);

const AuditChangesCell = ({ value }) => {
  const rows = useMemo(() => parseAuditDiff(value), [value]);
  // 셀을 클릭하면 상세 팝업이 뜨는데, 마우스가 그대로 멈춰 있어 hover 가 풀리지 않는다.
  // 그러면 툴팁이 팝업 위에 얹힌 채 남는다 → 클릭 시점에 직접 닫아야 한다.
  const [tooltipOpen, setTooltipOpen] = useState(false);

  if (isEmpty(rows)) {
    return <Box sx={{ color: "text.disabled" }}>{EMPTY_MARK}</Box>;
  }

  const previewRows = rows.slice(0, MAX_PREVIEW_ITEMS);
  const restCount = rows.length - previewRows.length;

  return (
    <Tooltip placement="bottom-start"
      open={tooltipOpen} onOpen={() => setTooltipOpen(true)} onClose={() => setTooltipOpen(false)}
      title={<Box sx={{ whiteSpace: "pre-wrap", fontSize: FS.SUB }}>{auditDiffToText(rows)}</Box>}>
      <Box onClick={() => setTooltipOpen(false)} sx={{
        display: "flex", alignItems: "center", gap: 1,
        height: "100%", overflow: "hidden", whiteSpace: "nowrap", cursor: "pointer",
      }}>
        {previewRows.map((row, index) => (
          <Box key={row.field} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1, minWidth: 0 }}>
            {index > 0 && <Box component="span" sx={{ color: "text.disabled" }}>·</Box>}
            <ChangeItem row={row} />
          </Box>
        ))}
        {restCount > 0 && (
          <Box component="span" sx={{ color: "text.secondary", fontWeight: 600, flexShrink: 0 }}>{`+${restCount}`}</Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default AuditChangesCell;
